import { useState, useEffect, useCallback } from "react";
import { Users, Phone, MessageCircle, Camera, Trash2, Clock, Search, Filter, UserCheck, UserX, User, Plus, X, Loader2, MapPin } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  new: { label: "جديد", color: "bg-blue-100 text-blue-700" },
  contacted: { label: "تم التواصل", color: "bg-amber-100 text-amber-700" },
  interested: { label: "مهتم", color: "bg-green-100 text-green-700" },
  booked: { label: "حجز موعد", color: "bg-purple-100 text-purple-700" },
  not_interested: { label: "غير مهتم", color: "bg-gray-100 text-gray-500" }
};

export default function LeadsPage() {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [editNotes, setEditNotes] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLead, setNewLead] = useState({ name: "", phone: "", notes: "" });
  const [adding, setAdding] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchLeads = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/leads`, { headers });
      if (res.ok) setLeads(await res.json());
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateStatus = async (id, status) => {
    try {
      await fetch(`${API_BASE}/api/leads/${id}`, { method: "PUT", headers, body: JSON.stringify({ status }) });
      fetchLeads();
    } catch (e) { console.error(e); }
  };

  const saveNotes = async (id) => {
    try {
      await fetch(`${API_BASE}/api/leads/${id}`, { method: "PUT", headers, body: JSON.stringify({ notes: editNotes[id] || "" }) });
      fetchLeads();
    } catch (e) { console.error(e); }
  };

  const deleteLead = async (id) => {
    try {
      await fetch(`${API_BASE}/api/leads/${id}`, { method: "DELETE", headers });
      fetchLeads();
    } catch (e) { console.error(e); }
  };

  const addLead = async () => {
    if (!newLead.name.trim() || !newLead.phone.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(`${API_BASE}/api/leads/manual`, {
        method: "POST", headers,
        body: JSON.stringify(newLead)
      });
      if (res.ok) {
        setNewLead({ name: "", phone: "", notes: "" });
        setShowAddForm(false);
        fetchLeads();
      }
    } catch (e) { console.error(e); }
    finally { setAdding(false); }
  };

  const formatDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return d; }
  };

  const filtered = leads.filter(l => {
    const matchSearch = !search || l.name?.includes(search) || l.phone?.includes(search);
    const matchStatus = filterStatus === "all" || l.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === "new").length,
    contacted: leads.filter(l => l.status === "contacted").length,
    interested: leads.filter(l => l.status === "interested").length,
    booked: leads.filter(l => l.status === "booked").length
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">العملاء المحتملين</h1>
          <p className="text-gray-500 text-sm mt-1">زوار الموقع + عملاء المعاينة</p>
        </div>
        <button onClick={() => setShowAddForm(!showAddForm)} className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-400 transition-colors" data-testid="add-lead-btn">
          {showAddForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showAddForm ? "إلغاء" : "إضافة عميل"}
        </button>
      </div>

      {/* Add Lead Form */}
      {showAddForm && (
        <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5" data-testid="add-lead-form">
          <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2"><MapPin className="w-5 h-5" />إضافة عميل جديد (معاينة بالمركز)</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <input value={newLead.name} onChange={e => setNewLead(p => ({ ...p, name: e.target.value }))} placeholder="اسم العميل" className="px-4 py-3 rounded-xl border border-amber-200 text-sm focus:border-amber-400 focus:outline-none bg-white" data-testid="add-lead-name" />
            <input value={newLead.phone} onChange={e => setNewLead(p => ({ ...p, phone: e.target.value }))} placeholder="رقم الهاتف" type="tel" dir="ltr" className="px-4 py-3 rounded-xl border border-amber-200 text-sm focus:border-amber-400 focus:outline-none bg-white text-left" data-testid="add-lead-phone" />
            <input value={newLead.notes} onChange={e => setNewLead(p => ({ ...p, notes: e.target.value }))} placeholder="ملاحظات (اختياري)" className="px-4 py-3 rounded-xl border border-amber-200 text-sm focus:border-amber-400 focus:outline-none bg-white" data-testid="add-lead-notes" />
          </div>
          <button onClick={addLead} disabled={adding || !newLead.name.trim() || !newLead.phone.trim()} className="mt-3 flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-400 disabled:opacity-40 transition-colors" data-testid="add-lead-submit">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            إضافة
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="leads-stats">
        {[
          { label: "الكل", value: stats.total, color: "bg-gray-50 text-gray-700" },
          { label: "جديد", value: stats.new, color: "bg-blue-50 text-blue-700" },
          { label: "تم التواصل", value: stats.contacted, color: "bg-amber-50 text-amber-700" },
          { label: "مهتم", value: stats.interested, color: "bg-green-50 text-green-700" },
          { label: "حجز", value: stats.booked, color: "bg-purple-50 text-purple-700" }
        ].map((s, i) => (
          <div key={i} className={`p-4 rounded-xl text-center ${s.color}`}>
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-xs mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search & Filter */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="بحث بالاسم أو الرقم..." className="w-full pr-10 pl-4 py-2.5 border rounded-xl text-sm focus:border-amber-400 focus:outline-none" data-testid="leads-search" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-4 py-2.5 border rounded-xl text-sm focus:border-amber-400 focus:outline-none bg-white" data-testid="leads-filter">
          <option value="all">كل الحالات</option>
          <option value="new">جديد</option>
          <option value="contacted">تم التواصل</option>
          <option value="interested">مهتم</option>
          <option value="booked">حجز موعد</option>
          <option value="not_interested">غير مهتم</option>
        </select>
      </div>

      {/* Leads List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Users className="w-16 h-16 mx-auto mb-3 opacity-40" />
          <p className="text-lg font-medium">لا يوجد عملاء محتملين</p>
          <p className="text-sm mt-1">سيظهرون هنا عندما يسجل زائر بياناته بالشات بوت أو تحليل الشعر</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((lead) => (
            <div key={lead.id} className="bg-white rounded-2xl border p-5 hover:shadow-md transition-shadow" data-testid={`lead-${lead.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                      <User className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">{lead.name}</h3>
                      <p className="text-sm text-gray-500 flex items-center gap-1" dir="ltr"><Phone className="w-3 h-3" />{lead.phone}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_MAP[lead.status]?.color || "bg-gray-100 text-gray-500"}`}>
                      {STATUS_MAP[lead.status]?.label || lead.status}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
                    <span className="flex items-center gap-1">
                      {lead.source === "chatbot" ? <MessageCircle className="w-3 h-3" /> : lead.source === "walk_in" ? <MapPin className="w-3 h-3" /> : <Camera className="w-3 h-3" />}
                      {lead.source === "chatbot" ? "شات بوت" : lead.source === "walk_in" ? "معاينة بالمركز" : "تحليل شعر"}
                    </span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(lead.created_at)}</span>
                  </div>

                  {/* Notes */}
                  <div className="flex gap-2">
                    <input
                      value={editNotes[lead.id] !== undefined ? editNotes[lead.id] : (lead.notes || "")}
                      onChange={e => setEditNotes(prev => ({ ...prev, [lead.id]: e.target.value }))}
                      onBlur={() => { if (editNotes[lead.id] !== undefined) saveNotes(lead.id); }}
                      placeholder="ملاحظات..."
                      className="flex-1 px-3 py-2 border rounded-lg text-sm focus:border-amber-400 focus:outline-none"
                      data-testid={`lead-notes-${lead.id}`}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1 shrink-0">
                  <select value={lead.status} onChange={e => updateStatus(lead.id, e.target.value)} className="px-2 py-1.5 border rounded-lg text-xs focus:outline-none bg-white" data-testid={`lead-status-${lead.id}`}>
                    <option value="new">جديد</option>
                    <option value="contacted">تم التواصل</option>
                    <option value="interested">مهتم</option>
                    <option value="booked">حجز موعد</option>
                    <option value="not_interested">غير مهتم</option>
                  </select>
                  <a href={`https://wa.me/${lead.phone?.replace(/^0/, "964")}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 px-2 py-1.5 bg-green-50 text-green-600 rounded-lg text-xs font-medium hover:bg-green-100 transition-colors" data-testid={`lead-whatsapp-${lead.id}`}>
                    <MessageCircle className="w-3 h-3" />واتساب
                  </a>
                  <button onClick={() => deleteLead(lead.id)} className="flex items-center justify-center p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors" data-testid={`lead-delete-${lead.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
